import { push } from "connected-react-router";
import * as api from "api";
import { modalType } from "components/ModalConductor";
import appActionTypes, { modal } from "containers/App/_actions";
import { fetchBudget } from "containers/Budget/_actions";
import ReactGA from "react-ga4";

export const actionTypes = {
    FARM_CHECKING: "FARM_CHECKING",
    FARM_TOUCHED: "FARM_TOUCHED",
    FARM_SAVED: "FARM_SAVED",
    FARM_SAVE_ERROR: "FARM_SAVE_ERROR",
    FARM_FETCHING: "FARM_FETCHING",
    FARM_FETCHED: "FARM_FETCHED",
    FARM_ACCESS_FETCHED: "FARM_ACCESS_FETCHED",
    FARM_CLEAR_STALE: "FARM_CLEAR_STALE",
    FARM_CLEAR: "FARM_CLEAR",
    FARM_NOT_FOUND: "FARM_NOT_FOUND",
    FARM_ACCESS_DENIED: "FARM_ACCESS_DENIED",
    TOGGLE_MY_FARM_SUCCESS: "TOGGLE_MY_FARM_SUCCESS",
    FARM_FETCH_CANCEL: "FARM_FETCH_CANCEL",
    REQUEST_PAYMENT_SUCCESS: "REQUEST_PAYMENT_SUCCESS",
    REQUEST_PAYMENT_STATUS_SUCCESS: "REQUEST_PAYMENT_STATUS_SUCCESS",
    PAYMENT_CLEAR: "PAYMENT_CLEAR",
    TOGGLE_PAYMENT_NOTIFICATION: "TOGGLE_PAYMENT_NOTIFICATION",
};
export default actionTypes;

// New Farm Exists Check
export function checkIfNewFarmExists(farm) {
    return (dispatch) => {
        dispatch({ type: actionTypes.FARM_CHECKING });
        dispatch(
            api.get({
                path: `search/existingfarms?name=${encodeURIComponent(farm.name)}&address=${encodeURIComponent(farm.address)}&ownerName=${encodeURIComponent(farm.ownerName)}&ownerEmail=${encodeURIComponent(farm.ownerEmail)}&identifiers=${encodeURIComponent(farm.identifiers)}&supplierNumber=${encodeURIComponent(farm.supplierNumber)}`,
                onSuccess: (response) => {
                    return (dispatch) => {
                        if (response.length > 0) {
                            const modalViewModel = { newFarm: farm, matchingFarms: response };
                            dispatch(
                                modal(
                                    modalType.POSSIBLE_DUPLICATE_FARMS,
                                    (matchingFarmId, newFarm) => {
                                        if (newFarm) {
                                            dispatch(createFarm(newFarm));
                                        } else {
                                            dispatch(push(`/app/farm/${matchingFarmId}`));
                                        }
                                    },
                                    modalViewModel
                                )
                            );
                        } else {
                            dispatch(createFarm(farm));
                        }
                    };
                },
                cancelActionType: actionTypes.FARM_CHECKING,
            })
        );
    };
}

// Create Farm
export function createFarm(farm, silentMode = false, postActions = []) {
    ReactGA.event({
        category: "Farm",
        action: "Create",
    });
    return (dispatch) => {
        dispatch({ type: actionTypes.FARM_TOUCHED, payload: { farm, isNew: true } });
        dispatch(
            api.post({
                path: `farms`,
                content: farm,
                onSuccess: (response) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.FARM_SAVED, payload: farm.id });
                        postActions.forEach((a) => dispatch(a));
                        if (!silentMode) dispatch(push(`/app/farm/${farm.id}`));
                    };
                },
                onFailure: (error) => {
                    return (dispatch) => {
                        dispatch({ type: actionTypes.FARM_SAVE_ERROR, payload: { farm, error } });
                    };
                },
                cancelActionType: actionTypes.FARM_TOUCHED,
                silentMode,
            })
        );
    };
}

export function clearPaymentTransaction(farmId) {
    return (dispatch) => {
        dispatch({ type: actionTypes.PAYMENT_CLEAR, payload: farmId });
    };
}

export function requestPaymentStatus(farm, transactionId) {
    return api.get({
        path: `farms/${farm.id}/payment/${transactionId}`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: actionTypes.REQUEST_PAYMENT_STATUS_SUCCESS, payload: { paymentTransaction: response, farm } });
            };
        },
        onFailure: (error = {}) => {
            return (dispatch) => {
                switch (error.status) {
                    case 400: {
                        error.handled = true;
                        const message = error.response && error.response.length > 0 ? error.response[0].errorMessage : "Unknown error";
                        dispatch({ type: actionTypes.REQUEST_PAYMENT_STATUS_SUCCESS, payload: { paymentTransaction: { transactionId, code: 100, message }, farm } });
                        break;
                    }
                    default:
                        break;
                }
            };
        },
    });
}

// New Farm Exists Check
export function requestPayment(farm, reference) {
    ReactGA.event({
        category: "Farm",
        action: "Request Payment",
    });

    return api.get({
        path: `farms/${farm.id}/requestpayment${reference ? "?reference=" + reference : ""}`,
        onBeforeSend: () => {
            return { type: appActionTypes.APP_MODAL_CLEAR_ERROR };
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: actionTypes.REQUEST_PAYMENT_SUCCESS, payload: { paymentTransaction: response, farm } });
            };
        },
        onFailure: (error = {}) => {
            return (dispatch) => {
                switch (error.status) {
                    case 400: {
                        error.handled = true;
                        const message = error.response && error.response.length > 0 ? error.response[0].errorMessage : "Unknown error";
                        dispatch({ type: actionTypes.REQUEST_PAYMENT_SUCCESS, payload: { paymentTransaction: { code: 100, message }, farm } });
                        break;
                    }
                    default:
                        break;
                }
            };
        },
    });
}

export function makeInvoicePayment(payment, silentMode = false) {
    ReactGA.event({
        category: "Farm",
        action: "Make Invoice Payment",
    });

    return api.post({
        path: `farms/${payment.farm.id}/invoicepayment`,
        content: payment,
        onBeforeSend: () => {
            return { type: appActionTypes.APP_MODAL_CLEAR_ERROR };
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
            };
        },
        onFailure: (error = {}) => {
            return (dispatch) => {
                switch (error.status) {
                    case 400: {
                        error.handled = true;
                        const message = error.response && error.response.length > 0 ? error.response[0].errorMessage : "Unknown error";
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR_REPEATABLE, payload: message });
                        break;
                    }
                    default:
                        break;
                }
            };
        },
    });
}

// Update Farm
export const updateFarm =
    (farm, options = {}) =>
    (dispatch) => {
        const { updateSoils, features, silentMode } = options;

        ReactGA.event({
            category: "Farm",
            action: "Update",
        });
        return new Promise((resolve, reject) => {
            dispatch({ type: actionTypes.FARM_TOUCHED, payload: { farm, isNew: false } });
            dispatch(
                api.put({
                    path: `farms/${farm.id}`,
                    content: { ...farm, updateSoils, features },
                    onSuccess: () => {
                        resolve();
                        return (dispatch) => {
                            dispatch({ type: actionTypes.FARM_SAVED, payload: farm.id });
                            if (!silentMode) dispatch(push(`/app/farm/${farm.id}`));
                        };
                    },
                    onFailure: (error) => {
                        reject(error);
                        return (dispatch) => {
                            dispatch({ type: actionTypes.FARM_SAVE_ERROR, payload: { farm, error } });
                        };
                    },
                    cancelActionType: actionTypes.FARM_TOUCHED,
                    silentMode,
                })
            );
        });
    };

// Delete Farm
export function deleteFarm(farm) {
    ReactGA.event({
        category: "Farm",
        action: "Delete",
    });
    return api.del({
        path: `farms/${farm.id}`,
        onBeforeSend: () => {
            return [{ type: actionTypes.FARM_TOUCHED, payload: { farm, isDeleted: true } }];
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: actionTypes.FARM_SAVED, payload: farm.id });
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(push(`/`));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                if (error.status === 404) {
                    // Already deleted.
                    dispatch({ type: actionTypes.FARM_SAVED, payload: farm.id });
                    dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                } else if (error.status === 403) {
                    dispatch({ type: actionTypes.FARM_TOUCHED, payload: { farm, isDirty: false, isDeleted: false } });
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You do not have permission to delete this farm." });
                } else if (error.offline) {
                    dispatch({ type: actionTypes.FARM_TOUCHED, payload: { farm, isDirty: false, isDeleted: false } });
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "Unable to delete this farm while offline. Please check your internet connection and try again." });
                } else {
                    dispatch({ type: actionTypes.FARM_TOUCHED, payload: { farm, isDirty: false, isDeleted: false } });
                    dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                }
            };
        },
    });
}

// Fetch Farm
export function fetchFarm(id, silentMode = false, budgetId, postActions = []) {
    return api.get({
        path: `farms/${id}`,
        onBeforeSend: () => {
            return { type: actionTypes.FARM_FETCHING, payload: { id } };
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: actionTypes.FARM_FETCHED, payload: response });
                postActions.forEach((a) => dispatch(a));
                if (budgetId) dispatch(fetchBudget(id, budgetId, false));
            };
        },
        onFailure: (error = {}) => {
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        error.handled = true;
                        dispatch(
                            api.get({
                                path: `search/farms/${id}`, // Gets a minimal details version of the farm (e.g. name, address, etc)
                                onSuccess: (response) => {
                                    return { type: actionTypes.FARM_ACCESS_DENIED, payload: response };
                                },
                                onFailure: (error) => {
                                    error.handled = true;
                                    return { type: actionTypes.FARM_ACCESS_DENIED, payload: { id } };
                                },
                            })
                        );
                        break;
                    case 404:
                        error.handled = true;
                        dispatch({ type: actionTypes.FARM_NOT_FOUND, payload: id });
                        break;
                    default:
                        break;
                }
            };
        },
        silentMode,
    });
}

export const fetchFarmAccess2 = (id) => (dispatch) => {
    return new Promise((resolve, reject) => {
        ReactGA.event({
            category: "Farms",
            action: "Fetch Farm Access",
        });
        return dispatch(
            api.get({
                path: `farms/${id}/access`,
                onSuccess: (response) => {
                    resolve(response);
                    return {};
                },
                onFailure: (error) => {
                    error.handled = true;
                    reject(error);
                    return {};
                },
            })
        );
    });
};

// Fetch Farm Access
export function fetchFarmAccess(id) {
    return api.get({
        path: `farms/${id}/access`,
        onSuccess: (response) => {
            return { type: actionTypes.FARM_ACCESS_FETCHED, payload: response };
        },
    });
}

// Sync Dirty Farms
export function syncDirtyFarms() {
    return (dispatch, getState) => {
        const { offlineFarms = [] } = getState();
        const dirtyOfflineFarms = offlineFarms.filter((f) => f.isDirty === true);
        dirtyOfflineFarms.forEach((offlineFarm) => {
            if (offlineFarm.isNew) dispatch(createFarm(offlineFarm.farm, true));
            else dispatch(updateFarm(offlineFarm.farm, { silentMode: true }));
        });
    };
}

// Toggle My Farm
export function toggleMyFarm(id, isMyFarm) {
    return api.put({
        path: `farms/${id}/myfarm/${isMyFarm}`,
        onSuccess: (response) => {
            return { type: actionTypes.TOGGLE_MY_FARM_SUCCESS, payload: { id, isMyFarm } };
        },
    });
}

// Grant Farm Access
export function grantFarmAccess(access) {
    ReactGA.event({
        category: "Farm",
        action: "Grant Access",
    });
    return api.put({
        path: `farms/${access.farmId}/access`,
        content: access,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(fetchFarmAccess(access.farmId));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 400:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR_REPEATABLE, payload: "Error sending invite. Please ensure the email address is valid." });
                        break;
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to grant the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}

// Revoke Farm Access Invitation
export function revokeFarmAccessInvitation(id, farmId, action) {
    ReactGA.event({
        category: "Farm",
        action: "Revoke Access Invitation",
    });
    return api.del({
        path: `farmAccessInvitations/${id}`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(fetchFarmAccess(farmId));
                action && dispatch(action);
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to revoke the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    case 404:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "This invitation is no longer valid." });
                        dispatch(fetchFarmAccess(farmId));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}

// Revoke Farm Access
export function setPaymentNotification(farmId, accountId, isActive) {
    console.log("set payment notification");
    ReactGA.event({
        category: "Farm",
        action: "Set Payment Notification",
    });
    return api.put({
        path: `farms/${farmId}/access/paymentNotification`,
        content: {
            accountId,
            id: farmId,
            isActive,
        },
        onBeforeSend: () => {
            return (dispatch) => {
                dispatch({ type: actionTypes.TOGGLE_PAYMENT_NOTIFICATION, payload: { farmId, accountId } });
            };
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(fetchFarmAccess(farmId));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to perform this action." });
                        dispatch(push(`/`));
                        break;
                    case 404:
                        dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                        dispatch(fetchFarmAccess(farmId));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}
export function revokeFarmAccess(farmId, farmAccess, isMyAccess) {
    ReactGA.event({
        category: "Farm",
        action: "Revoke Access",
    });
    return api.del({
        path: `farms/${farmId}/access`,
        content: {
            accountId: farmAccess.accountId,
            userId: farmAccess.userId,
            role: farmAccess.role,
            id: farmId,
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                if (isMyAccess) dispatch(push(`/`));
                else dispatch(fetchFarmAccess(farmId));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to revoke the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    case 404:
                        dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                        if (isMyAccess) dispatch(push(`/`));
                        else dispatch(fetchFarmAccess(farmId));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}

export const cloneFarmToStudent = (farm) => {
    ReactGA.event({
        category: "Budget",
        action: "Copy to student",
    });

    return api.get({
        path: `farms/${farm.id}/studentcopy`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch(push(`/app/farm/${response}`));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
            };
        },
    });
};

// Farm Publication Access
export function fetchPublicationAccess(id) {
    return api.get({
        path: `farms/${id}/publications/access`,
        onSuccess: (response) => {
            return { type: actionTypes.FARM_ACCESS_FETCHED, payload: response };
        },
    });
}

// Grant Farm Publication Access
export function grantPublicationAccess(access) {
    ReactGA.event({
        category: "Farm",
        action: "Grant Access",
    });
    return api.put({
        path: `farms/${access.farmId}/access`,
        content: access,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(fetchPublicationAccess(access.farmId));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 400:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR_REPEATABLE, payload: "Error sending invite. Please ensure the email address is valid." });
                        break;
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to grant the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}

// Revoke Farm Publication Access Invitation
export function revokePublicationAccessInvitation(id, farmId, action) {
    ReactGA.event({
        category: "Farm",
        action: "Revoke Access Invitation",
    });
    return api.del({
        path: `farmAccessInvitations/${id}`,
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                dispatch(fetchPublicationAccess(farmId));
                action && dispatch(action);
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to revoke the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    case 404:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "This invitation is no longer valid." });
                        dispatch(fetchPublicationAccess(farmId));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}

// Revoke Farm Publication Access
export function revokePublicationAccess(farmId, farmAccess, isMyAccess) {
    ReactGA.event({
        category: "Farm",
        action: "Revoke Access",
    });
    return api.del({
        path: `farms/${farmId}/access`,
        content: {
            accountId: farmAccess.accountId,
            userId: farmAccess.userId,
            role: farmAccess.role,
            id: farmId,
        },
        onSuccess: (response) => {
            return (dispatch) => {
                dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                if (isMyAccess) dispatch(push(`/`));
                else dispatch(fetchPublicationAccess(farmId));
            };
        },
        onFailure: (error) => {
            error.handled = true;
            return (dispatch) => {
                switch (error.status) {
                    case 403:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "You are not authorised to revoke the requested farm access." });
                        dispatch(push(`/`));
                        break;
                    case 404:
                        dispatch({ type: appActionTypes.APP_CLOSE_MODAL });
                        if (isMyAccess) dispatch(push(`/`));
                        else dispatch(fetchPublicationAccess(farmId));
                        break;
                    default:
                        dispatch({ type: appActionTypes.APP_MODAL_ERROR, payload: "An unexpected error has occured. Please try again or call support if the problem persists." });
                        break;
                }
            };
        },
    });
}
